import React, { Suspense } from 'react'
import { Button, Spinner, Input, Menu } from '@design-systems/canvas-ui'
import Icon, { ChevronUpIcon } from '@design-systems/react-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleDot } from '@fortawesome/free-regular-svg-icons'
import {
  ROUTE_VETTING_SUMMARY_LOCATION,
  FASTTRACK_TOOLTIP,
  ENABLE_APPROVE_TOOLTIP,
  ENABLE_REJECT_TOOLTIP,
  ADMIN_CONTROL_TYPE,
  ADMIN_ID_CHATBOT_LCM,
  BUTTON,
  STATUS_APPROVE_AND_START_VFA,
  STATUS_APPROVE_AND_BYPASS_VFA,
  STATUS_APPROVE_BUT_HOLD,
  STATUS_REJECTED,
  STATUS_RFI3_APPROVED,
  APPROVE_GROUP,
} from '../../../../common/commonConstants'
import { CustomTooltip } from 'mvscommoncomponents'
import { getControlVisibility } from '../../../../common'
import { getApprovalButtonName } from '../../../../common/helper'

const ChatBotWidgetLazy = React.lazy(
  () => import('../../../../globalComponents/Chatbot/ChatBotWidget'),
)

const ALL_APPROVE = [
  STATUS_APPROVE_AND_START_VFA,
  STATUS_APPROVE_AND_BYPASS_VFA,
  STATUS_APPROVE_BUT_HOLD,
]

const Rfi3FooterActions = ({
  isRfi3SaveInProgress,
  isRfi3SaveFailed,
  isRfi3DataFetchFailed,
  isRfi3DataFetchProgress,
  rfi3Template,
  rfi3Data,
  isReadonly,
  isRfi3ActionAdminEnabled,
  isFastTrackAvailable,
  isFastTrackInProgress,
  isFastTrackFailed,
  isRfiActionInProgress,
  isRfiActionFailed,
  userAction,
  navigate,
  userSettings,
  onFastTrackClick,
  onRejectClick,
  onApproveClick,
  isRfi3ActionButtonEnabled,
  isPageLoading,
  env,
}) => {
  const showAutosave =
    !isRfi3SaveFailed &&
    !isRfi3DataFetchFailed &&
    !isRfi3DataFetchProgress &&
    rfi3Template &&
    rfi3Data

  const loading = isPageLoading?.()

  return (
    <div className="rfi3-footer-actions">
      {isRfi3SaveInProgress && !isRfi3SaveFailed && (
        <Spinner size="sm" className="hc8-ml-6x" />
      )}

      {showAutosave && (
        <div className="align-centre-left hc8-ml-6x">
          <div id="spotlight-autosave">
            <FontAwesomeIcon icon={faCircleDot} className="blinker" />
          </div>
          <Input.Label className="hc8-ml-2x hc8-mb-none hc8-fs-150 font-weight-500">
            Autosaved
          </Input.Label>
        </div>
      )}

      {getControlVisibility(
        ADMIN_ID_CHATBOT_LCM,
        ADMIN_CONTROL_TYPE,
        userSettings,
      ) && (
        <Suspense fallback={null}>
          <ChatBotWidgetLazy />
        </Suspense>
      )}

      <div className="rfi3-footer-actions__actions">
        <Button
          className="rfi3-footer-actions__button"
          data-testid="btn-action-cancel"
          variant="ghost"
          size="md"
          onClick={() => navigate(ROUTE_VETTING_SUMMARY_LOCATION)}
        >
          Close
        </Button>

        {isRfi3ActionAdminEnabled &&
          !isReadonly &&
          !loading &&
          !isRfi3DataFetchFailed && (
            <>
              {rfi3Template?.show_fast_track && (
                <CustomTooltip
                  title={isFastTrackAvailable ? FASTTRACK_TOOLTIP : ''}
                  placement="top"
                  arrow
                  component={
                    <Button
                      className="rfi3-footer-actions__button"
                      variant="secondary"
                      size="md"
                      onClick={onFastTrackClick}
                      isDisabled={!isFastTrackAvailable}
                    >
                      {isFastTrackInProgress && !isFastTrackFailed ? (
                        <Spinner size="sm" />
                      ) : (
                        'Fast Track'
                      )}
                    </Button>
                  }
                />
              )}

              <CustomTooltip
                title={
                  !rfi3Data?.all_rfi3_answered ? ENABLE_REJECT_TOOLTIP : ''
                }
                placement="top"
                arrow
                component={
                  <Button
                    className="rfi3-footer-actions__button"
                    variant="primary"
                    size="md"
                    isDisabled={
                      isRfi3SaveInProgress ||
                      !isRfi3ActionButtonEnabled(STATUS_REJECTED)
                    }
                    onClick={onRejectClick}
                  >
                    {isRfiActionInProgress &&
                    !isRfiActionFailed &&
                    userAction === STATUS_REJECTED ? (
                      <Spinner size="sm" />
                    ) : (
                      'Reject'
                    )}
                  </Button>
                }
              />

              {rfi3Template?.approval_action?.type?.toLowerCase() === BUTTON ? (
                <CustomTooltip
                  title={
                    !rfi3Data?.all_rfi3_answered ? ENABLE_APPROVE_TOOLTIP : ''
                  }
                  placement="top"
                  arrow
                  component={
                    <Button
                      className="rfi3-footer-actions__button"
                      variant="primary"
                      size="md"
                      isDisabled={
                        isRfi3SaveInProgress ||
                        !isRfi3ActionButtonEnabled(STATUS_RFI3_APPROVED)
                      }
                      onClick={() => onApproveClick(STATUS_RFI3_APPROVED)}
                    >
                      {isRfiActionInProgress &&
                      !isRfiActionFailed &&
                      userAction === STATUS_RFI3_APPROVED ? (
                        <Spinner size="sm" />
                      ) : (
                        'Approve'
                      )}
                    </Button>
                  }
                />
              ) : (
                <CustomTooltip
                  title={
                    !rfi3Data?.all_rfi3_answered ? ENABLE_APPROVE_TOOLTIP : ''
                  }
                  placement="top"
                  arrow
                  component={
                    <Menu size="md" location="top-right">
                      <Menu.Toggle
                        size="md"
                        variant="primary"
                        className="rfi3-footer-actions__approve-toggle"
                        isDisabled={
                          isRfi3SaveInProgress ||
                          !isRfi3ActionButtonEnabled(APPROVE_GROUP)
                        }
                      >
                        <div className="rfi3-footer-actions__approve-content">
                          {isRfiActionInProgress &&
                          !isRfiActionFailed &&
                          ALL_APPROVE.includes(userAction) ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <span>Approve</span>
                              <Icon
                                svg={ChevronUpIcon}
                                className="rfi3-footer-actions__approve-icon"
                                size="xs"
                              />
                            </>
                          )}
                        </div>
                      </Menu.Toggle>

                      {rfi3Template.approval_action?.options?.map((option) => {
                        const status = env?.mfelcm?.vettingStatusMap?.[option]

                        const isApproveButHoldCurrent =
                          rfi3Data?.rfi3_vetting_status?.toUpperCase() ===
                          'APPROVED BUT HOLD'

                        const isApproveButHoldOption =
                          status?.toUpperCase() === 'APPROVE BUT HOLD'

                        const shouldDisableApproveButHold =
                          isApproveButHoldCurrent && isApproveButHoldOption

                        return (
                          <Menu.Item
                            key={option}
                            className={`hc8-fs-100 hc8-txt-capitalize ${
                              shouldDisableApproveButHold
                                ? 'hc8-clr-disabled'
                                : 'hc8-clr-brand'
                            }`}
                            isDisabled={
                              !isRfi3ActionButtonEnabled(
                                status?.toUpperCase(),
                              ) || shouldDisableApproveButHold
                            }
                            onClick={() =>
                              onApproveClick(status?.toUpperCase())
                            }
                          >
                            {getApprovalButtonName(status)}
                          </Menu.Item>
                        )
                      })}
                    </Menu>
                  }
                />
              )}
            </>
          )}
      </div>
    </div>
  )
}

export default React.memo(Rfi3FooterActions)
